import React from 'react';
import styled from 'styled-components';
import { Body1, Body5, Body7, Container } from '@ui';
import { CloseButton } from '@ui/buttons';
import { DefList } from './DefList';
import { AliasChip, ChipRow } from './AliasEditor';
import { StatusPill } from './StatusPill';
import { CatalogItemForm, FORM_MODES, itemAliases } from './CatalogItemForm';
import { formatCatalogStatus, EM_DASH } from './format';
import { CATALOG_STATUS_TOKENS, tokenFor } from './statusTokens';
import { Mono, SectionHead } from './styles';

// One catalog item, in the vault's right-side drawer: read-only for members, the edit form for
// admins underneath the record. Escape closes without touching the list's filters.

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 10000;
`;

// 480px rather than the vault drawer's 420px: the edit form's FieldGrid (minmax(200px, 1fr)) needs
// 416px of content to go two-up, and 420px minus the 24px padding does not reach it.
const DrawerWrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 480px;
  max-width: 90vw;
  overflow-y: auto;

  background: ${({ theme }) => theme.themeColors.appBackground};
  border-left: 1px solid ${({ theme }) => theme.themeColors.border};
  box-shadow: -2px 0 8px 1px rgba(0, 0, 0, 0.12);
  padding: 24px;
  z-index: 10001;
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;

  svg {
    height: 14px;
    width: 14px;
  }

  g {
    fill: ${({ theme }) => theme.themeColors.text};
  }
`;

export const CatalogItemDrawer = ({ item, canAdmin = false, onClose, onSaved }) => {
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!item) return null;

  const aliases = itemAliases(item);
  const rows = [
    { label: 'Section', value: item.section },
    { label: 'Sub-section', value: item.sub_section },
    { label: 'Value kind', value: item.default_value_kind },
    { label: 'Units', value: item.default_units },
    { label: 'Sign convention', value: item.normalization_or_sign_convention || item.sign_convention },
    { label: 'Required level', value: item.required_level },
    { label: 'Source', value: item.source },
    { label: 'Template', value: item.template_version },
  ];

  return (
    <>
      <Backdrop onClick={onClose} data-testid="catalog-item-drawer-backdrop" />
      <DrawerWrapper role="dialog" aria-label={item.canonical_item_label || item.canonical_item_code}>
        <CloseButtonWrapper>
          <CloseButton onClick={onClose} size={14} />
        </CloseButtonWrapper>

        <Container right={32}>
          <Body1 bold>{item.canonical_item_label || item.canonical_item_code}</Body1>
        </Container>
        <Container top={4} row verticallyCenter gap={12}>
          <Mono>{item.canonical_item_code}</Mono>
          <StatusPill token={tokenFor(CATALOG_STATUS_TOKENS, item.status)} label={formatCatalogStatus(item.status)} />
        </Container>

        <Container top={20}>
          <DefList rows={rows} />
        </Container>

        <Container top={20}>
          <SectionHead heading="Aliases" subtext="Source labels that map to this item." />
          <Container top={8}>
            {aliases.length === 0 ? (
              <Body7 isLowContrast>{EM_DASH}</Body7>
            ) : (
              <ChipRow role="list" aria-label="Aliases">
                {aliases.map((alias) => (
                  <AliasChip key={alias} role="listitem">
                    <Body7>{alias}</Body7>
                  </AliasChip>
                ))}
              </ChipRow>
            )}
          </Container>
        </Container>

        {canAdmin && (
          <Container top={24}>
            <SectionHead heading="Edit item" subtext="Changes refresh the catalog's aliases and embeddings." />
            <Container top={12}>
              <CatalogItemForm mode={FORM_MODES.EDIT} item={item} onSaved={onSaved} />
            </Container>
          </Container>
        )}
        {!canAdmin && (
          <Container top={24}>
            <Body5 isLowContrast>Only an administrator can edit catalog items.</Body5>
          </Container>
        )}
      </DrawerWrapper>
    </>
  );
};
